import React, { useEffect, Children, createElement } from 'react';
import { SEO } from '../components/seo/SEO';
import { BreadcrumbSchema } from '../components/seo/Schema';
import { InteractivePipeline } from '../components/sections/InteractivePipeline';
import { SimpleEngineOverview } from '../components/sections/SimpleEngineOverview';
import { ClientLogos } from '../components/sections/ClientLogos';
import { motion } from 'framer-motion';
export function HowItWorks() {
  useEffect(() => {
    // Load Wistia player script
    const script = document.createElement('script');
    script.src = 'https://fast.wistia.com/player.js';
    script.async = true;
    document.head.appendChild(script);
    // Load Wistia embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/yfxrcvgduq.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    return () => {
      // Cleanup scripts on unmount
      if (script.parentNode) document.head.removeChild(script);
      if (embedScript.parentNode) document.head.removeChild(embedScript);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <>
      <SEO title="How The HIRE Advantage Works | D2D Recruiting System" description="See how the HIRE Advantage combines recruiting, onboarding, and retention frameworks to help door-to-door and direct sales companies scale predictably." canonical="/how-it-works" ogTitle="How Our D2D Recruiting System Works" ogDescription="Discover the proven system that helps door-to-door companies recruit, retain, and scale high-performing sales teams." ogImage="https://uploadthingy.s3.us-west-1.amazonaws.com/vZQb1BRCvwHNhKSytwSdua/Screenshot_2025-11-25_at_12.06.50_AM.png?v=2" keywords="sales recruiting system, door to door sales hiring, recruit door to door sales reps, sales team scaling system" data-id="element-1414" />

      <BreadcrumbSchema items={[{
      name: 'Home',
      url: '/'
    }, {
      name: 'How It Works',
      url: '/how-it-works'
    }]} data-id="element-1415" />

      <div className="pt-20" data-id="element-1416">
        {/* Hero */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-br from-gray-50 to-white overflow-hidden" data-id="element-1417">
          <div className="container-custom" data-id="element-1418">
            <motion.div className="max-w-4xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1419">
              <motion.div className="inline-block bg-mint/10 text-mint-dark px-4 py-2 rounded text-sm font-semibold mb-6" variants={itemVariants} data-id="element-1420">
                The HIRE Advantage
              </motion.div>
              <motion.h1 className="mb-6" variants={itemVariants} data-id="element-1421">
                How It Works
              </motion.h1>
              <motion.p className="text-body-large leading-relaxed mb-8" variants={itemVariants} data-id="element-1422">
                This is a consistent, systematic approach to recruiting that
                takes out the guesswork. It delivers predictable results and
                positive ROI, eliminates leadership burnout, and helps you
                capture the top talent that is currently going to your
                competitors.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Wistia Video */}
        <section className="py-12 md:py-16 bg-white" data-id="element-1423">
          <div className="container-custom" data-id="element-1424">
            <motion.div className="max-w-4xl mx-auto" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1]
          }} data-id="element-1425">
              <div className="bg-deep-blue rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-1426">
                <div className="aspect-video bg-deep-blue-light rounded-lg overflow-hidden border-2 border-mint/20" data-id="element-1427">
                  <style data-id="element-1428">{`
                    wistia-player[media-id='yfxrcvgduq']:not(:defined) { 
                      background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/yfxrcvgduq/swatch'); 
                      display: block; 
                      filter: blur(5px); 
                      padding-top: 56.25%; 
                    }
                  `}</style>
                  <wistia-player media-id="yfxrcvgduq" aspect="1.7777777777777777" className="w-full h-full" data-id="element-1429"></wistia-player>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Interactive Pipeline */}
        <InteractivePipeline data-id="element-1430" />

        {/* Simple Engine Overview */}
        <SimpleEngineOverview data-id="element-1431" />

        {/* Client Logos */}
        <ClientLogos data-id="element-1432" />

        {/* Final CTA */}
        <section className="section-padding bg-white" data-id="element-1433">
          <div className="container-custom" data-id="element-1434">
            <motion.div className="max-w-3xl mx-auto text-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1]
          }} data-id="element-1435">
              <h2 className="mb-6" data-id="element-1436">Ready to See the System?</h2>
              <p className="text-body-large mb-8 leading-relaxed" data-id="element-1437">
                Every system is custom-built for your market and your goals.
                Let's map yours out.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1438">
                <a href="/book-call" className="inline-block bg-mint hover:bg-mint-dark text-deep-blue font-bold px-8 py-4 rounded-lg transition-colors shadow-lg" data-id="element-1439">
                  Schedule Your Strategy Call →
                </a>
                <a href="/case-studies" className="inline-block border-2 border-deep-blue text-deep-blue hover:bg-deep-blue hover:text-white font-bold px-8 py-4 rounded-lg transition-colors" data-id="element-1440">
                  View Case Studies
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>;
}